/* eslint-disable jsdoc/require-jsdoc */
import { createApp, h, onMounted, Ref, unref, VNode } from 'vue';
import InfoModal from '../InfoModal.vue';
/**
 * Iconの種類
 */
type IconType = '' | 'info' | 'error' | 'loading';
/**
 * colorStyleの種類
 */
type ColorStyle = 'primary' | 'error';

/**
 * プログレスバー表示用オプション
 */
interface InfoModalProgressOptions {
  /**
   * プログレスラベル
   */
  label?: InfoModalSlot;
  /**
   * プログレスメッセージ
   */
  message?: InfoModalSlot;
  /**
   * プログレス値
   */
  value?: Ref<number> | number;
  /**
   * プログレス最大値
   */
  max?: number;
}

/**
 * InfoModalのスロットに渡すヘルパー
 */
interface InfoModalSlotHelpers {
  /**
   * 戻るボタンの動作を実行する
   */
  onBack: () => void;
  /**
   * 次へボタンの動作を実行する
   */
  onNext: () => void;
}

/**
 * InfoModalのスロットに使用できる型
 */
type InfoModalSlot = string | VNode | ((helpers: InfoModalSlotHelpers) => string | VNode);

/**
 * モーダルのオプション
 */
export interface InfoModalOptions {
  /**
   * タイトルスロットに表示する内容
   */
  modalTitle?: InfoModalSlot;
  /**
   * 説明スロットに表示する内容
   */
  modalDescription?: InfoModalSlot;
  /**
   * モーダル本体に表示する内容
   */
  modalContent?: InfoModalSlot;
  /**
   * フッタースロットに表示する内容
   */
  modalFooter?: InfoModalSlot;
  /**
   * 戻る・次へボタンの間に表示する内容
   */
  modalBetweenButtons?: InfoModalSlot;
  /**
   * プログレス表示の設定
   */
  progress?: InfoModalProgressOptions;
  /**
   * 戻るボタンのラベル
   */
  backButtonLabel?: string;
  /**
   * 次へボタンのラベル
   */
  nextButtonLabel?: string;
  /**
   * ヘッダーに表示するアイコンの種類
   */
  icon?: IconType;
  /**
   * 基調となるカラースタイル
   */
  colorStyle?: ColorStyle;
  /**
   * モーダル外のクリックで閉じられるかどうか
   */
  enableOutsideClick?: boolean;
  /**
   * モーダル幅（px）
   */
  modalWidth?: number;
  /**
   * 次へボタンを無効化するかどうか
   */
  isDisabledNextButton?: boolean;
  /**
   * モーダル内のスクロールを無効化するかどうか
   */
  isDisabledScroll?: boolean;
  /**
   * テストで使用するdata-testid（E2E用）
   */
  testId?: string;
  /**
   * 説明エリアを非表示にするかどうか
   */
  noDescription?: boolean;
  /**
   * モーダル初期化時に実行する処理。ヘルパーを引数で受け取る
   */
  onInitialized?: (helpers: InfoModalSlotHelpers) => void;
  /**
   * モーダルマウント後に実行する処理。ヘルパーを引数で受け取る
   */
  onMounted?: (helpers: InfoModalSlotHelpers) => void;
  /**
   * モーダルコンテンツを中央寄せにするかどうか
   */
  isCenterContent?: boolean;
  /**
   * キーボード操作を受け付けるか
   */
  useKeyboard?: boolean;
}

/**
 * InfoModalを表示する
 * @param options - InfoModalのオプション
 * @returns onBack実行でfalse、onNext実行でtrueを返すPromise
 */
export const showInfoModal = (options: InfoModalOptions): Promise<boolean> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const close = (result: boolean) => {
      app.unmount();
      document.body.removeChild(container);
      resolve(result);
    };

    const helpers: InfoModalSlotHelpers = {
      onBack: () => {
        close(false);
      },
      onNext: () => {
        close(true);
      },
    };
    options.onInitialized?.(helpers);

    const resolveSlot = (slot?: InfoModalSlot) => {
      if (typeof slot === 'function') {
        return slot(helpers);
      }

      return slot;
    };

    const app = createApp({
      setup() {
        onMounted(() => {
          options.onMounted?.(helpers);
        });
      },
      /**
       * レンダラー
       */
      render() {
        return h(
          InfoModal,
          {
            modalState: true,
            backButtonLabel: options.backButtonLabel,
            nextButtonLabel: options.nextButtonLabel,
            icon: options.icon,
            colorStyle: options.colorStyle,
            enableOutsideClick: options.enableOutsideClick,
            modalWidth: options.modalWidth,
            isDisabledNextButton: options.isDisabledNextButton,
            isDisabledScroll: options.isDisabledScroll,
            testId: options.testId,
            noDescription: options.noDescription,
            progressValue: options.progress ? unref(options.progress.value ?? 0) : undefined,
            progressMax: options.progress?.max,
            isCenterContent: options.isCenterContent,
            useKeyboard: options.useKeyboard,

            /**
             * InfoModalを閉じる（onBack実行時）
             */
            onOnBack: () => {
              helpers.onBack();
            },
            /**
             * InfoModalを次に進める（onNext実行時）
             */
            onOnNext: () => {
              helpers.onNext();
            },
          },
          {
            title: () => resolveSlot(options.modalTitle),
            description: () => resolveSlot(options.modalDescription),
            default: () => resolveSlot(options.modalContent),
            footer: () => resolveSlot(options.modalFooter),
            betweenButtons: () => resolveSlot(options.modalBetweenButtons),
            progressLabel: () => resolveSlot(options.progress?.label),
            progressMessage: () => resolveSlot(options.progress?.message),
          }
        );
      },
    });

    app.mount(container);
  });
};
